import {
  IconTool,
  IconSchool,
  IconMicroscope,
  IconHammer,
  IconChevronRight,
  IconBuildingBank,
  IconBook2,
  IconListCheck,
  IconUsers,
  IconPalette,
  IconTags,
  IconMail,
  IconBuildingCommunity,
  IconSearch,
  IconStar,
  IconSparkles,
  IconChartBar,
  IconHistory,
  IconUserShield,
  IconUserCog,
  IconCreditCard,
  IconCoin,
  IconGift,
  IconPercentage,
  IconFileInvoice,
  type Icon,
} from '@tabler/icons-react';
import { Fragment, useState } from 'react';
import { Link, useParams, useRouterState } from '@tanstack/react-router';
import { useCategories } from '../../hooks/useCategories.js';
import { useAgencySignups } from '../../hooks/useAgencies.js';
import { useAuthStore } from '../../lib/auth.js';
import { buildSidebar, type SidebarIconKey } from '../../lib/sidebar-links.js';
import { cn } from '../../lib/cn.js';

/** Category-tree icons, keyed by the API's `icon` string (dynamic categories). */
const ICON_MAP: Record<string, Icon> = {
  tool: IconTool,
  school: IconSchool,
  microscope: IconMicroscope,
  hammer: IconHammer,
};

/** Static nav-link icons, keyed by SidebarItem.icon from buildSidebar. */
const SIDEBAR_ICONS: Record<SidebarIconKey, Icon> = {
  search: IconSearch,
  sparkles: IconSparkles,
  star: IconStar,
  coin: IconCoin,
  'file-invoice': IconFileInvoice,
  'building-bank': IconBuildingBank,
  book2: IconBook2,
  'list-check': IconListCheck,
  'building-community': IconBuildingCommunity,
  gift: IconGift,
  'chart-bar': IconChartBar,
  history: IconHistory,
  'user-shield': IconUserShield,
  'user-cog': IconUserCog,
  percentage: IconPercentage,
  users: IconUsers,
  palette: IconPalette,
  tags: IconTags,
  'credit-card': IconCreditCard,
  mail: IconMail,
};

/** Desktop sidebar — hidden below lg, where the mobile drawer takes over. */
export function Sidebar() {
  return (
    <aside className="hidden lg:block w-64 shrink-0 border-r border-line bg-cream/40 min-h-[calc(100vh-4rem)]">
      <SidebarNav />
    </aside>
  );
}

/**
 * The nav body, shared by the desktop sidebar and the mobile drawer. When
 * `onNavigate` is provided (drawer), tapping any link closes the drawer; the
 * category expand/collapse buttons are left alone (delegated check for <a>).
 *
 * This is a DUMB RENDERER of buildSidebar(user) (Brief 8c): every link's
 * visibility, its section, and the order all live in lib/sidebar-links.ts, which
 * is pure and unit-tested. Everything below that is NOT a function of the user
 * alone stays here — the dynamic category tree, the Agencies pending badge,
 * active state, and the footer.
 */
export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { data, isLoading, error } = useCategories();
  const user = useAuthStore((s) => s.user);
  const role = user?.role;

  // Pending agency applications awaiting review — only fetched for super admins.
  const pendingSignups = useAgencySignups(role === 'super_admin');
  const pendingCount = pendingSignups.data?.signups.length ?? 0;

  // Pull category param from the URL when we're on a /app/courses/$category[/...] route
  // so the sidebar auto-expands the matching section.
  const params = useParams({ strict: false }) as { category?: string; subCategory?: string };
  const location = useRouterState({ select: (s) => s.location.pathname });

  const [manualExpanded, setManualExpanded] = useState<string | null>(null);
  const expanded = manualExpanded ?? params.category ?? null;

  const sections = buildSidebar(user);

  return (
    <>
      <nav
        className="p-4 space-y-1"
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('a')) onNavigate?.();
        }}
      >
        {sections.map((section, sIdx) => (
          <Fragment key={section.key}>
            <div className={cn('px-2 pb-2', sIdx === 0 ? 'pt-1' : 'pt-6')}>
              <div className="text-[11px] uppercase tracking-[0.12em] text-muted font-semibold">
                {section.heading}
              </div>
            </div>

            {section.items.map((item) => {
              const Icon = SIDEBAR_ICONS[item.icon];
              // Same rule every link used before: lit while the pathname is
              // inside this link's target. Equivalent to the old per-link `isX`
              // booleans, which all read location.startsWith(<this path>).
              const active = location.startsWith(item.to);
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left',
                    section.key === 'catalogue' && 'mb-1',
                    'text-sm font-medium transition',
                    active
                      ? 'bg-white text-navy shadow-card'
                      : 'text-ink-soft hover:bg-white hover:text-navy hover:shadow-card',
                  )}
                >
                  <Icon
                    size={18}
                    className={cn('shrink-0', active ? 'text-gold' : 'text-muted')}
                  />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.key === 'admin-agencies' && pendingCount > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full px-1.5 text-[11px] font-semibold"
                      style={{ backgroundColor: '#0D2B4A', color: '#FBF8F2' }}
                      title={`${pendingCount} application${pendingCount === 1 ? '' : 's'} awaiting review`}
                    >
                      {pendingCount}
                    </span>
                  )}
                </Link>
              );
            })}

            {/* The dynamic category tree lives at the tail of the Catalogue
                section: data-driven (useCategories), so it is the renderer's,
                not buildSidebar's. */}
            {section.key === 'catalogue' && (
              <>
                {isLoading && (
                  <div className="px-3 py-2 text-sm text-muted">Loading categories…</div>
                )}

                {error && (
                  <div className="px-3 py-2 text-sm text-danger">Couldn't load categories</div>
                )}

                {data?.categories.map((c) => {
                  const Icon = ICON_MAP[c.icon] ?? IconTool;
                  const isOpen = expanded === c.category;
                  return (
                    <div key={`category-${c.category}`}>
                      <button
                        onClick={() => setManualExpanded(isOpen ? null : c.category)}
                        className={cn(
                          'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left',
                          'text-sm font-medium text-ink-soft',
                          'hover:bg-white hover:text-navy hover:shadow-card transition',
                          isOpen && 'bg-white text-navy shadow-card',
                        )}
                      >
                        <Icon size={18} className={cn('shrink-0', isOpen ? 'text-gold' : 'text-muted')} />
                        <span className="flex-1 truncate">{c.label}</span>
                        <IconChevronRight
                          size={14}
                          className={cn(
                            'text-muted-soft transition-transform',
                            isOpen && 'rotate-90',
                          )}
                        />
                      </button>

                      {isOpen && c.subCategories.length > 0 && (
                        <ul className="ml-7 mt-1 space-y-0.5 border-l border-line pl-3">
                          {c.subCategories.map((sub) => {
                            const subPath = `/app/courses/${c.category}/${sub.slug}`;
                            const isActive = location.startsWith(subPath);
                            return (
                              <li key={sub.slug}>
                                <Link
                                  to="/app/courses/$category/$subCategory"
                                  params={{ category: c.category, subCategory: sub.slug }}
                                  className={cn(
                                    'block w-full text-left px-2 py-1.5 rounded text-sm transition',
                                    isActive
                                      ? 'text-navy font-medium bg-cream-deep/40'
                                      : 'text-muted hover:text-ink-soft hover:bg-white/60',
                                  )}
                                >
                                  {sub.label}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </Fragment>
        ))}
      </nav>

      <div className="px-6 py-4 mt-4 border-t border-line">
        <p className="text-xs text-muted leading-relaxed">
          {role === 'super_admin'
            ? 'Curate the master catalogue from the Admin section above.'
            : 'Master courses are live. Send course overviews straight to students.'}
        </p>
      </div>
    </>
  );
}
